import BodyText from 'LuxKit/Typography/BodyText'
import Modal from 'LuxKit/Modal/Modal'
import Clickable from 'components/Common/Clickable'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useCallback, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import FormatCurrency from 'components/Common/FormatCurrency'
import StarRatingStar from 'components/Common/StarRatingStar'
import ExperiencesPassportItemDetailsModal from './ExperiencesPassportItemDetailsModal'
import ExperiencesPassportVoucherModal from './ExperiencesPassportVoucherModal'
import type { ExperiencesPassportVoucherModalResult } from './ExperiencesPassportVoucherModal'
import type { ExperiencesPassport, ExperiencesPassportInclusion } from './experiencesPassportTypes'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import { getImageUrl } from 'lib/image/imageUtils'
import { trackExperiencesPassportEvent } from 'analytics/snowplow/events/experiencesPassportEvents'

const InclusionRow = styled.div`
  display: grid;
  grid-template-columns: ${rem(56)} minmax(0, 1fr) ${rem(24)};
  gap: ${rem(12)};
  align-items: center;
  width: 100%;
  padding: ${rem(12)} 0;
  border-bottom: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  text-align: start;

  &:last-child {
    border-bottom: 0;
  }

  &.variant-my-escapes {
    grid-template-columns: ${rem(56)} minmax(0, 1fr) minmax(${rem(52)}, max-content);
  }
`

const Thumbnail = styled.div`
  display: grid;
  place-items: center;
  width: ${rem(56)};
  height: ${rem(56)};
  border-radius: ${rem(2)};
  color: ${props => props.theme.palette.highlight.primary.darkForeground};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  background-size: cover;
  background-position: center;
`

interface Props {
  passport: ExperiencesPassport
  primaryActionTo?: string
  variant?: 'offer-page' | 'search-tile' | 'my-escapes'
}

function ExperiencesPassportListModal({ passport, primaryActionTo, variant = 'offer-page' }: Props) {
  const showModal = useContext(ModalContext)
  const modalContext = useModalElementContext()
  const isMyEscapes = variant === 'my-escapes'
  const trackingSurface = variant === 'search-tile' ? 'search_tile' : variant === 'my-escapes' ? 'my_escapes' : 'offer_page'

  const openItemDetails = useCallback((inclusion: ExperiencesPassportInclusion) => {
    trackExperiencesPassportEvent('item_opened', trackingSurface)
    showModal(<ExperiencesPassportItemDetailsModal inclusion={inclusion} />)
  }, [showModal, trackingSurface])
  const openVoucher = useCallback(async(inclusion: ExperiencesPassportInclusion) => {
    trackExperiencesPassportEvent('item_opened', trackingSurface)
    const result = await showModal<ExperiencesPassportVoucherModalResult>(<ExperiencesPassportVoucherModal inclusion={inclusion} />)
    if (result === 'backToMyEscapes') {
      modalContext?.resolve()
    }
  }, [modalContext, showModal, trackingSurface])

  return <Modal
    primaryActionText={primaryActionTo ? <FormattedMessage
      id="ki4HFHxmPOxFm2FjOSi-"
      defaultMessage="View offer"
      
    /> : undefined}
    primaryActionTo={primaryActionTo}
    title={
      <FormattedMessage
        id="zkZljzRwx21S91yeBnUZ"
        defaultMessage="LE Exclusive Extras"
        
      />
    }
  >
    <VerticalSpacer gap={16}>
      <BodyText variant="medium">
        {isMyEscapes ? <FormattedMessage
          id="tzFLdL2Ay-OmJt50MVfC"
          defaultMessage="Enjoy curated inclusions from Luxury Escapes as part of your package."
          
        /> : <FormattedMessage
          id="IJwjgmxgWNzzx5R7n5lU"
          defaultMessage="Enjoy curated complimentary experiences from Luxury Escapes, included on select rooms. Check the offer for more details."
          
        />}
      </BodyText>
      <VerticalSpacer gap={4}>
        {passport.inclusions.map(inclusion => {
          const isUsed = inclusion.redemptionStatus === 'used'
          const canRedeem = isMyEscapes && !isUsed && !!inclusion.voucherCode
          const Icon = getOfferFeatureIcon(inclusion.symbol)
          const thumbnailImageUrl = inclusion.imageUrl || (inclusion.image?.id ? getImageUrl(inclusion.image.id, {
            width: 112,
            height: 112,
            quality: 'good',
          }) : undefined)

          return <InclusionRow
            as={isMyEscapes ? 'div' : Clickable}
            className={`variant-${variant}`}
            key={inclusion.id}
            onClick={isMyEscapes ? undefined : () => openItemDetails(inclusion)}
          >
            <Thumbnail style={thumbnailImageUrl ? { backgroundImage: `url(${encodeURI(thumbnailImageUrl)})` } : undefined}>
              {!thumbnailImageUrl && <Icon />}
            </Thumbnail>
            <Group direction="vertical" gap={4}>
              <BodyText variant="medium">{inclusion.title}</BodyText>
              {(!!inclusion.capacityText || !!inclusion.value) && <BodyText variant="medium">
                {!!inclusion.capacityText && !!inclusion.value && <FormattedMessage
                  id="FnkCjr1gzT9ulBxY6It1"
                  defaultMessage="{capacityText} · {valueText}"
                  
                  values={{
                    capacityText: inclusion.capacityText,
                    valueText: <BodyText as="span" variant="medium" colour="highlight-primary-dark" weight="bold">
                      <FormattedMessage
                        id="ahL5nRQr2cL4cH1QbaGz"
                        defaultMessage="valued up to {value}"
                        
                        values={{
                          value: <FormatCurrency
                            value={inclusion.value}
                            currency={inclusion.valueCurrencyCode}
                            format="roundedDollar"
                          />,
                        }}
                      />
                    </BodyText>,
                  }}
                />}
                {!!inclusion.capacityText && !inclusion.value && inclusion.capacityText}
                {!inclusion.capacityText && !!inclusion.value && <FormattedMessage
                  id="ahL5nRQr2cL4cH1QbaGz"
                  defaultMessage="valued up to {value}"
                  
                  values={{
                    value: <BodyText as="span" variant="medium" colour="highlight-primary-dark" weight="bold">
                      <FormatCurrency
                        value={inclusion.value}
                        currency={inclusion.valueCurrencyCode}
                        format="roundedDollar"
                      />
                    </BodyText>,
                  }}
                />}
              </BodyText>}
              {!!inclusion.rating && <Group direction="horizontal" gap={4} verticalAlign="center">
                <StarRatingStar kind="filled" size="XS" />
                <BodyText variant="small">
                  <FormattedMessage
                    id="bfByel_k25Q8ntz_NCuw"
                    defaultMessage="{rating} ({reviewsText})"
                    
                    values={{
                      rating: inclusion.rating,
                      reviewsText: inclusion.reviewsText,
                    }}
                  />
                </BodyText>
              </Group>}
            </Group>
            {isMyEscapes && canRedeem && <TextLink size="small" onClick={() => openVoucher(inclusion)}>
              <FormattedMessage
                id="ALvO4xsPX3ay8ZcC7RKj"
                defaultMessage="Redeem"
                
              />
            </TextLink>}
            {isMyEscapes && isUsed && <BodyText variant="small" colour="normal-contrast">
              <FormattedMessage
                id="aWaD3a09q6I5y3E0Lt9T"
                defaultMessage="Redeemed"
                
              />
            </BodyText>}
            {!isMyEscapes && <LineAngleRightBIcon size="M" />}
          </InclusionRow>
        })}
      </VerticalSpacer>
    </VerticalSpacer>
  </Modal>
}

export default ExperiencesPassportListModal
